import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { CornerUpLeft, Pencil } from 'lucide-react';

interface ComposerDockProps {
  /** Collapsed field placeholder, e.g. "Write a reply to Ananya…" */
  placeholder: string;
  /** Label on the collapsed button: "Reply" | "New note" */
  buttonLabel: string;
  /** 'brass' for reply, 'ink' for note — the note button must not look like a send. */
  buttonTone?: 'brass' | 'ink';
  /** Keyboard hint shown in the field, e.g. "R" / "N". Also the open shortcut. */
  shortcut?: string;
  /** Right-hand caption in the collapsed field (suggestion count, visibility warning). */
  hint?: ReactNode;
  /** True when a draft exists — the dock says so instead of the placeholder. */
  hasDraft?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

/**
 * The collapsed composer dock.
 *
 * Owns only the collapsed/expanded transition — it knows nothing about sending.
 * Collapsed, it is a clickable *field* plus a button: the field is what makes
 * the dock read as writable, the button is what makes it discoverable. A bare
 * button would look like a command, not a place to type.
 *
 * The one rule that makes hiding a composer safe: **collapsing never discards
 * content.** The draft lives in the parent's state, and a collapsed dock holding
 * one says so, so nothing ever looks lost.
 */
export function ComposerDock({
  placeholder,
  buttonLabel,
  buttonTone = 'brass',
  shortcut,
  hint,
  hasDraft = false,
  open,
  onOpenChange,
  children,
}: ComposerDockProps) {
  const fieldRef = useRef<HTMLDivElement>(null);
  // Set while collapsing so focus returns to the field the user came from.
  const returnFocus = useRef(false);

  const collapse = useCallback(() => {
    returnFocus.current = true;
    onOpenChange(false);
  }, [onOpenChange]);

  // Escape collapses; focus goes back to the collapsed field.
  useEffect(() => {
    if (!open) {
      if (returnFocus.current) {
        returnFocus.current = false;
        fieldRef.current?.focus();
      }
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        collapse();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, collapse]);

  // The shortcut opens the dock, but only when the user isn't already typing —
  // otherwise "r" in a search box would open the composer.
  useEffect(() => {
    if (!shortcut || open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      ) {
        return;
      }
      // Don't steal the key from a modal / dialog that's holding focus.
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
      if (e.key.toLowerCase() === shortcut.toLowerCase()) {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcut, open, onOpenChange]);

  const Icon = buttonTone === 'ink' ? Pencil : CornerUpLeft;

  return (
    <div className="flex-none border-t border-[var(--co-line)] bg-[var(--co-surface)] px-6 pb-3.5 pt-3">
      {open ? (
        children
      ) : (
        <div className="flex items-center gap-2.5">
          {/* One button semantically — the whole field opens the editor. */}
          <div
            ref={fieldRef}
            role="button"
            tabIndex={0}
            aria-expanded={false}
            onClick={() => onOpenChange(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenChange(true);
              }
            }}
            className="flex h-10 min-w-0 flex-1 cursor-text items-center gap-2.5 border border-[var(--co-line)] bg-[var(--co-raised)] px-3 transition-colors duration-[120ms] hover:border-[var(--co-brass)]"
          >
            <Icon className="h-3.5 w-3.5 flex-none text-[var(--co-ink-4)]" />
            <span
              className={clsx(
                'min-w-0 truncate text-[13.5px]',
                hasDraft ? 'text-[var(--co-ink-2)]' : 'text-[var(--co-ink-4)]',
              )}
            >
              {hasDraft ? 'Continue your draft…' : placeholder}
            </span>

            {hasDraft && (
              <span className="flex-none rounded-[2px] border border-[var(--co-brass)] bg-[var(--co-brass-wash)] px-[5px] py-[2px] font-mono text-[9px] font-semibold uppercase tracking-[.14em] text-[var(--co-brass-ink)]">
                Draft
              </span>
            )}

            <span className="ml-auto flex flex-none items-center gap-2.5">
              {hint}
              {shortcut && (
                <span className="hidden border border-[var(--co-line)] px-[5px] py-[2px] font-mono text-[9.5px] font-medium uppercase leading-none text-[var(--co-ink-4)] sm:inline-block">
                  {shortcut}
                </span>
              )}
            </span>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(true)}
            className={clsx(
              'h-10 flex-none px-[17px] text-[12.5px] font-semibold transition-colors duration-[120ms]',
              buttonTone === 'ink'
                ? 'border border-[var(--co-ink)] bg-[var(--co-ink)] text-[var(--co-ink-invert)]'
                : 'border border-[var(--co-brass)] bg-[var(--co-brass)] text-[var(--co-brass-on)] hover:bg-[var(--co-brass-hover)]',
            )}
          >
            {buttonLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default ComposerDock;
